import { useState, useEffect, useCallback, useMemo } from "react";
import { Service } from "../components/templates/design1/components/OurServices";
import { EmblaCarouselType } from "embla-carousel";

export function useGroupedServices(
  data: string[] | undefined,
  newData: Service[]
) {
  const filteredServices = useMemo(
    () => newData.filter((service) => data?.includes(service.assessor)),
    [data, newData]
  );

  /**
   * Balanced grouping with maximum 3 rows/groups constraint
   * Ensures even distribution while never exceeding 3 groups
   */
  const groupServicesBalanced = useCallback(
    (services: Service[]): Service[][] => {
      const totalServices = services.length;

      if (totalServices === 0) return [];
      if (totalServices <= 5) return [services];

      // Calculate optimal number of groups with MAX 3 constraint
      let optimalGroups: number;

      if (totalServices <= 10) {
        optimalGroups = 2; // 2 groups for 6-10 items
      } else if (totalServices <= 18) {
        optimalGroups = 3; // 3 groups for 11-18 items
      } else {
        // For 19+ items, always use 3 groups (max constraint)
        optimalGroups = 3;
      }

      // Calculate sizes with minimal difference
      const baseSize = Math.floor(totalServices / optimalGroups);
      const remainder = totalServices % optimalGroups;

      // Create groups with sizes differing by at most 1
      const groups: Service[][] = [];
      let currentIndex = 0;

      for (let i = 0; i < optimalGroups; i++) {
        const groupSize = baseSize + (i < remainder ? 1 : 0);
        groups.push(services.slice(currentIndex, currentIndex + groupSize));
        currentIndex += groupSize;
      }

      return groups.filter((group) => group.length > 0);
    },
    []
  );

  const slidersData = useMemo(() => {
    return groupServicesBalanced(filteredServices);
    // Alternative: return groupServicesWithSizeLimit(filteredServices, 8);
  }, [filteredServices, groupServicesBalanced]);

  const [emblaApis, setEmblaApis] = useState<(EmblaCarouselType | null)[]>(() =>
    Array(slidersData.length).fill(null)
  );

  const setEmblaApi = useCallback(
    (index: number) => (api: EmblaCarouselType) => {
      setEmblaApis((prev) => {
        if (prev[index] === api) return prev;
        const newApis = [...prev];
        newApis[index] = api;
        return newApis;
      });
    },
    []
  );

  useEffect(() => {
    setEmblaApis((prev) => {
      if (prev.length === slidersData.length) return prev;
      return Array(slidersData.length).fill(null);
    });
  }, [slidersData.length]);

  const scrollPrev = useCallback(() => {
    emblaApis.forEach((api) => api?.scrollPrev());
  }, [emblaApis]);

  const scrollNext = useCallback(() => {
    emblaApis.forEach((api) => api?.scrollNext());
  }, [emblaApis]);

  // Enhanced debug information with max groups validation
  const debugInfo = useMemo(() => {
    if (process.env.NODE_ENV === "development") {
      const groupSizes = slidersData.map((group) => group.length);
      return {
        totalServices: filteredServices.length,
        numberOfGroups: slidersData.length,
        maxGroupsConstraint:
          slidersData.length <= 3 ? "✅ PASSED" : "❌ FAILED",
        groupSizes,
        sizeDifference:
          groupSizes.length > 0
            ? Math.max(...groupSizes) - Math.min(...groupSizes)
            : 0,
        averageGroupSize: (
          filteredServices.length / slidersData.length
        ).toFixed(1),
        distribution: slidersData.map((group, index) => ({
          group: index + 1,
          size: group.length,
          items: group.map((service) => service.assessor || "unnamed"),
        })),
      };
    }
    return null;
  }, [filteredServices.length, slidersData]);

  return {
    filteredServices,
    slidersData,
    emblaApis,
    setEmblaApi,
    scrollPrev,
    scrollNext,
    // Include debug info in development
    ...(process.env.NODE_ENV === "development" && { debugInfo }),
  };
}

// Updated utility function for testing with max 3 groups constraint
export function testGroupingStrategies(services: Service[]) {
  const strategies = {
    balancedMax3: (services: Service[]) => {
      const totalServices = services.length;
      if (totalServices === 0) return [];
      if (totalServices <= 5) return [services];

      let optimalGroups: number;
      if (totalServices <= 10) optimalGroups = 2;
      else if (totalServices <= 18) optimalGroups = 3;
      else optimalGroups = 3; // MAX 3 constraint

      const baseSize = Math.floor(totalServices / optimalGroups);
      const remainder = totalServices % optimalGroups;

      const groups: Service[][] = [];
      let currentIndex = 0;

      for (let i = 0; i < optimalGroups; i++) {
        const groupSize = baseSize + (i < remainder ? 1 : 0);
        groups.push(services.slice(currentIndex, currentIndex + groupSize));
        currentIndex += groupSize;
      }

      return groups.filter((group) => group.length > 0);
    },
    sizeLimitedMax3: (services: Service[], maxPerGroup: number = 8) => {
      const totalServices = services.length;
      if (totalServices === 0) return [];
      if (totalServices <= 5) return [services];

      let optimalGroups: number;
      if (totalServices <= maxPerGroup) optimalGroups = 1;
      else if (totalServices <= maxPerGroup * 2) optimalGroups = 2;
      else optimalGroups = 3; // MAX 3 constraint

      const baseSize = Math.floor(totalServices / optimalGroups);
      const remainder = totalServices % optimalGroups;

      const groups: Service[][] = [];
      let currentIndex = 0;

      for (let i = 0; i < optimalGroups; i++) {
        const groupSize = baseSize + (i < remainder ? 1 : 0);
        groups.push(services.slice(currentIndex, currentIndex + groupSize));
        currentIndex += groupSize;
      }

      return groups.filter((group) => group.length > 0);
    },
  };

  const results = Object.entries(strategies).map(([name, strategy]) => {
    const groups = strategy(services);
    const groupSizes = groups.map((g) => g.length);
    return {
      strategy: name,
      groups: groups.length,
      maxGroupsValid: groups.length <= 3 ? "✅" : "❌",
      sizes: groupSizes,
      maxDifference:
        groupSizes.length > 0
          ? Math.max(...groupSizes) - Math.min(...groupSizes)
          : 0,
      distribution: groupSizes.join("-"),
      largestGroup: Math.max(...groupSizes, 0),
      averageSize: (services.length / groups.length).toFixed(1),
    };
  });

  return results;
}

// Example usage and test cases
export function demonstrateGrouping() {
  if (process.env.NODE_ENV === "development") {
    // Test cases to validate max 3 groups constraint
    const testCases = [
      { name: "3 services", count: 3 },
      { name: "7 services", count: 7 },
      { name: "12 services", count: 12 },
      { name: "20 services", count: 20 },
      { name: "35 services", count: 35 },
      { name: "50 services", count: 50 },
    ];

    console.log("=== Max 3 Groups Constraint Test ===");

    testCases.forEach((testCase) => {
      const mockServices = Array.from({ length: testCase.count }, (_, i) => ({
        assessor: `Service ${i + 1}`,
        // Add other required Service properties as needed
      })) as Service[];

      const results = testGroupingStrategies(mockServices);
      console.log(`\n${testCase.name}:`);
      results.forEach((result) => {
        console.log(
          `  ${result.strategy}: ${result.groups} groups ${result.maxGroupsValid} - Distribution: ${result.distribution}`
        );
      });
    });
  }
}
